import { motion } from 'framer-motion';
import { Phone, ChevronRight } from 'lucide-react';

const HERO_IMAGE = 'https://images.pexels.com/photos/48784/roof-plate-tiles-brick-black-48784.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260';

const HERO_STATS = [
  { value: '25+', label: 'Jahre Erfahrung', accent: false },
  { value: 'VELUX', label: 'Zertifizierter Experte', accent: true },
  { value: '11', label: 'Fachbereiche', accent: false },
  { value: '100%', label: 'Meisterqualität', accent: false },
];

export const HeroSection = ({ settings }) => {
  return (
    <section id="hero" className="relative overflow-hidden" data-testid="hero-section">
      {/* Mobile BG */}
      <div className="absolute inset-0 lg:hidden">
        <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0077D4]/80 via-[#0077D4]/70 to-[#0077D4]/90" />
      </div>

      <div className="relative z-10 flex flex-col lg:flex-row lg:min-h-screen">
      {/* Content */}
      <div className="w-full lg:w-[58%] lg:bg-[#0077D4] flex items-center relative">
        {/* Decorative accent bar */}
        <div className="hidden lg:block absolute left-0 top-1/3 w-1.5 h-28 bg-[#CC2229]" />

        <div className="px-6 sm:px-12 lg:px-20 xl:px-28 pt-28 pb-10 lg:py-0 w-full max-w-3xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#CC2229]" />
              <span className="section-label text-[#CC2229]">
                {settings?.company_subtitle || 'Meisterbetrieb'} seit 1998
              </span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-[0.95] tracking-tight uppercase">
              {settings?.hero_headline ? settings.hero_headline : (
                <>Ihr Dach in<br/><span className="relative inline-block">besten Händen<span className="absolute -bottom-2 left-0 w-full h-1 bg-[#CC2229]" /></span></>
              )}
            </h1>

            <p className="text-slate-300 text-base sm:text-lg mt-8 leading-relaxed max-w-lg">
              {settings?.hero_subheadline || 'Qualität und Zuverlässigkeit aus Dresden — Dachneueindeckung, Sanierung, Dachfenster und mehr.'}
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mt-8 lg:mt-10">
              <a
                href="#kontakt"
                onClick={e => { e.preventDefault(); document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="group bg-[#CC2229] text-white px-8 py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#a31b20] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-xl inline-flex items-center gap-2"
                data-testid="hero-cta-primary"
              >
                {settings?.hero_cta_text || 'Kostenloses Angebot'}
                <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href={`tel:${settings?.phone || '0351-2038530'}`}
                className="border-2 border-white/30 text-white px-8 py-4 font-bold tracking-wider text-sm hover:border-white hover:bg-white/5 transition-all duration-300 inline-flex items-center gap-3"
                data-testid="hero-cta-phone"
              >
                <Phone size={18} /> {settings?.phone || '0351-2038530'}
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Desktop Image */}
      <div className="hidden lg:block w-[42%] relative">
        <div className="absolute inset-0 hero-clip">
          <img src={HERO_IMAGE} alt="Dacharbeiten" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0077D4]/30 to-transparent" />
        </div>
      </div>
      </div>

      {/* Stats Bar - fließend auf Mobile, absolut auf Desktop */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="relative lg:absolute lg:bottom-0 lg:left-0 lg:right-0 z-20"
      >
        <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="grid grid-cols-4 bg-white shadow-2xl border-t-4 border-[#CC2229]">
            {HERO_STATS.map((stat, i) => (
              <div key={i} className={`p-3 sm:p-5 lg:p-6 ${i < 3 ? 'border-r border-slate-100' : ''}`} data-testid={`hero-stat-${i}`}>
                <span className={`font-heading text-base sm:text-2xl lg:text-3xl font-black ${stat.accent ? 'text-[#CC2229]' : 'text-[#0077D4]'}`}>
                  {stat.value}
                </span>
                <p className="text-slate-400 text-[10px] sm:text-xs mt-0.5 sm:mt-1 uppercase tracking-wider leading-tight">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};
